import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import getJob from '@cartwhl/common/api/getJob';
import getJobs from '@cartwhl/common/api/getJobs';
import putSessionName from '@cartwhl/common/api/putSessionName';
import { Job } from '@cartwhl/common/types';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';

import { webSocketStore } from 'stores';

type HistoryStoreState = {
  jobs?: Job[]; // These are not complete job records, that's why we load the full record when accordion opens
  fullJobs: Job[];
  nextToken?: string;
  loading: boolean;
  done: boolean;
  searchTerm: string;
  searchResults: Job[];
};

const defaultValues: HistoryStoreState = {
  jobs: undefined,
  fullJobs: [],
  nextToken: undefined,
  loading: false,
  done: false,
  searchTerm: '',
  searchResults: [],
};

export const state = proxy<HistoryStoreState>(defaultValues);

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'History', enabled: true });
}

///// Actions:
export function setFavorite(
  jobID: string,
  animationNumber: string | number,
  isFavorited: boolean,
) {
  const job = state.jobs?.find((job) => job.jobID === jobID);
  if (job) {
    job.variationInfoByID[animationNumber].favorite = isFavorited;
  }
  const fullJob = state.fullJobs.find((job) => job.jobID === jobID);
  if (fullJob) {
    fullJob.variationInfoByID[animationNumber].favorite = isFavorited;
  }
}

export function setFeedback(
  jobID: string,
  animationNumber: string | number,
  feedback: 'positive' | 'negative',
) {
  const job = state.jobs?.find((job) => job.jobID === jobID);
  if (job) {
    job.variationInfoByID[animationNumber].feedback = feedback;
  }
  const fullJob = state.fullJobs.find((job) => job.jobID === jobID);
  if (fullJob) {
    fullJob.variationInfoByID[animationNumber].feedback = feedback;
  }
}

export async function setSessionName(jobID: string, sessionName: string) {
  const { data, error } = await putSessionName(jobID, sessionName);
  if (error) {
    await handleGenericErrors(error, { redirectTo: 'studio' });
  } else {
    const job = state.jobs?.find((job) => job.jobID === jobID);
    if (job) {
      job.sessionName = sessionName;
    }
    const fullJob = state.fullJobs.find((job) => job.jobID === jobID);
    if (fullJob) {
      fullJob.sessionName = sessionName;
    }
  }
}

export async function fetchHistory(limit?: number, sortAscending?: boolean) {
  if (state.loading || state.done) return;

  state.loading = true;
  const { data, error } = await getJobs(limit, state.nextToken, sortAscending);
  if (error) {
    await handleGenericErrors(error, { redirectTo: 'studio' });
  }
  state.loading = false;

  if (!data) return;

  state.jobs = state.jobs || [];

  for (const j of data.jobs || []) {
    if (j.hideFromHistory) {
      continue;
    }
    const index = state.jobs.findIndex(
      (existing) => existing.jobID === j.jobID,
    );
    if (index !== -1) {
      state.jobs[index] = j;
    } else {
      state.jobs.push(j);
    }
  }

  sortJobsByCreationDate();

  state.nextToken = data.nextToken || undefined;
  state.done = !data.nextToken;
}

export function setSearchTerm(value: string) {
  state.searchTerm = value;

  if (state.searchTerm.length < 2) {
    state.searchResults = [];
  } else {
    state.searchResults =
      state.jobs?.filter((job) => {
        return (
          job.prompt.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
          job.sessionName.toLowerCase().includes(state.searchTerm.toLowerCase())
        );
      }) || [];
  }
}

export async function updateOrAddJob(jobID: string, jobObject?: Job) {
  if (!state.jobs) return;

  let job = jobObject;
  if (!job) {
    // If there is no job object we load it from the server (this happens because websockets only send JobID)
    const { data } = await getJob(jobID);
    job = data;
  }
  if (!job) {
    // Couldn't find a job!
    return;
  }
  const index = state.jobs.findIndex((j) => {
    return j.jobID === jobID;
  });
  if (index === -1) {
    state.jobs.push(job);
  } else {
    state.jobs[index] = job;
  }

  const index2 = state.fullJobs.findIndex((j) => {
    return j.jobID === jobID;
  });
  if (index2 === -1) {
    state.fullJobs.push(job);
  } else {
    state.fullJobs[index2] = job;
  }

  sortJobsByCreationDate();
}

export async function loadFullJob(jobID: string) {
  const { data, error } = await getJob(jobID);
  if (error) console.log('Error: ', error);
  if (!data) return;

  state.fullJobs = state.fullJobs || [];
  const index = state.fullJobs.findIndex((j) => {
    return j.jobID === jobID;
  });
  if (index === -1) {
    state.fullJobs.push(data);
  } else {
    state.fullJobs[index] = data;
  }
}

export async function reset() {
  state.jobs = defaultValues.jobs;
  state.nextToken = defaultValues.nextToken;
  state.loading = defaultValues.loading;
  state.done = defaultValues.done;
  state.searchTerm = defaultValues.searchTerm;
  state.searchResults = defaultValues.searchResults;
}

function sortJobsByCreationDate() {
  state.jobs?.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

//// WEBSOCKET EVENTS
webSocketStore.on('GENERATING_PK', (data) => {
  updateOrAddJob(data.jobID);
});

webSocketStore.on('BVH_READY', (data) => {
  updateOrAddJob(data.jobID);
});

webSocketStore.on('FAILED', (data) => {
  updateOrAddJob(data.jobID);
});
